import React, { useState, useEffect } from 'react';
import WidgetRenderer from './WidgetRenderer';

const PreviewModal = ({ page, sections, isOpen, onClose, onExportJSON }) => {
  const [showJSON, setShowJSON] = useState(false);
  const [activeDevice, setActiveDevice] = useState('desktop');

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const formData = {
    id: page.id,
    title: page.title,
    content: sections,
    settings: page.settings,
    lastModified: page.lastModified,
  };

  const handleDownloadJSON = () => {
    const dataStr = JSON.stringify(formData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${page.title || 'form'}_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCopyJSON = () => {
    const dataStr = JSON.stringify(formData, null, 2);
    navigator.clipboard.writeText(dataStr).then(() => {
      alert('JSON copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy JSON');
    });
  };

  const getDeviceWidth = () => {
    switch(activeDevice) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      case 'desktop': return '100%';
      default: return '100%';
    }
  };

  return (
    <div 
      className="preview-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        animation: 'fadeIn 0.3s ease'
      }}
      onClick={onClose}
    >
      <div 
        className="preview-modal-content"
        style={{
          position: 'relative',
          width: '90%',
          maxWidth: '1200px',
          height: '90vh',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideUp 0.3s ease',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div 
          className="preview-modal-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fff'
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#333' }}>
              {page.title || 'Form Preview'}
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
              Last modified: {new Date(formData.lastModified).toLocaleString()}
            </p>
          </div>
          <button 
            className="preview-modal-close" 
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#f0f0f0',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              color: '#666'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e0e0e0';
              e.currentTarget.style.color = '#333';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
              e.currentTarget.style.color = '#666';
            }}
            title="Close Preview"
          >
            ✕
          </button>
        </div>

        {/* Device Switcher (only for preview mode) */}
        {!showJSON && (
          <div 
            className="preview-device-switcher"
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '12px',
              padding: '12px 24px',
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            <button
              onClick={() => setActiveDevice('desktop')}
              style={{
                padding: '5px 10px',
                borderRadius: '6px',
                border: '1px solid',
                borderColor: activeDevice === 'desktop' ? '#007bff' : '#ddd',
                backgroundColor: activeDevice === 'desktop' ? '#007bff' : '#fff',
                color: activeDevice === 'desktop' ? '#fff' : '#666',
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.2s ease'
              }}
            >
              💻 Desktop
            </button>
            <button
              onClick={() => setActiveDevice('tablet')}
              style={{
                padding: '5px 10px',
                borderRadius: '6px',
                border: '1px solid',
                borderColor: activeDevice === 'tablet' ? '#007bff' : '#ddd',
                backgroundColor: activeDevice === 'tablet' ? '#007bff' : '#fff',
                color: activeDevice === 'tablet' ? '#fff' : '#666',
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.2s ease'
              }}
            >
              📱 Tablet
            </button>
            <button
              onClick={() => setActiveDevice('mobile')}
              style={{
                padding: '5px 10px',
                borderRadius: '6px',
                border: '1px solid',
                borderColor: activeDevice === 'mobile' ? '#007bff' : '#ddd',
                backgroundColor: activeDevice === 'mobile' ? '#007bff' : '#fff',
                color: activeDevice === 'mobile' ? '#fff' : '#666',
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.2s ease'
              }}
            >
              📱 Mobile
            </button>
          </div>
        )}

        {/* Tabs */}
        <div 
          className="preview-modal-tabs"
          style={{
            display: 'flex',
            borderBottom: '2px solid #e0e0e0',
            backgroundColor: '#fff'
          }}
        >
          <button 
            className={`preview-tab ${!showJSON ? 'active' : ''}`}
            onClick={() => setShowJSON(false)}
            style={{
              padding: '12px 20px',
              border: 'none',
              backgroundColor: 'transparent',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              color: !showJSON ? '#007bff' : '#666',
              borderBottom: !showJSON ? '2px solid #007bff' : '2px solid transparent',
              transition: 'all 0.2s ease'
            }}
          >
            👁️ Preview
          </button>
          <button 
            className={`preview-tab ${showJSON ? 'active' : ''}`}
            onClick={() => setShowJSON(true)}
            style={{
              padding: '12px 20px',
              border: 'none',
              backgroundColor: 'transparent',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              color: showJSON ? '#007bff' : '#666',
              borderBottom: showJSON ? '2px solid #007bff' : '2px solid transparent',
              transition: 'all 0.2s ease'
            }}
          >
            {`{ }`} JSON Data
          </button>
        </div>

        {/* Modal Body */}
        <div 
          className="preview-modal-body"
          style={{
            flex: 1,
            overflow: 'auto',
            backgroundColor: '#f5f5f5',
            position: 'relative'
          }}
        >
          {!showJSON ? (
            <div 
              className="form-preview-container"
              style={{
                padding: '24px',
                minHeight: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <div style={{ width: getDeviceWidth(), transition: 'width 0.3s ease' }}>
                {sections.map((section, index) => (
                  <div 
                    key={section.id} 
                    className="section-preview"
                    style={{
                      backgroundColor: section.settings?.backgroundColor || '#ffffff',
                      padding: `${section.settings?.padding?.top || 50}px ${section.settings?.padding?.right || 0}px ${section.settings?.padding?.bottom || 50}px ${section.settings?.padding?.left || 0}px`,
                      margin: `${section.settings?.margin?.top || 0}px ${section.settings?.margin?.right || 0}px ${section.settings?.margin?.bottom || 0}px ${section.settings?.margin?.left || 0}px`,
                      borderRadius: section.settings?.borderRadius || 0,
                      boxShadow: section.settings?.boxShadow || 'none',
                      position: 'relative'
                    }}
                  >
                    {/* Section label in preview (for development) */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        fontSize: '11px',
                        color: '#999',
                        backgroundColor: '#f0f0f0',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        opacity: 0.7
                      }}
                    >
                      Section {index + 1}
                    </div>
                    
                    <div className="columns-container"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: section.columns?.map(col => `${col.width}%`).join(' ') || '1fr',
                        gap: section.settings?.gap || '20px',
                        maxWidth: '100%'
                      }}
                    >
                      {section.columns?.map((column) => (
                        <div 
                          key={column.id} 
                          className="column-preview"
                          style={{
                            padding: `${column.settings?.padding?.top || 20}px ${column.settings?.padding?.right || 20}px ${column.settings?.padding?.bottom || 20}px ${column.settings?.padding?.left || 20}px`,
                            backgroundColor: column.settings?.backgroundColor || 'transparent',
                            borderRadius: column.settings?.borderRadius || 0
                          }}
                        >
                          {column.widgets && column.widgets.length > 0 ? (
                            column.widgets.map((widget) => (
                              <div key={widget.id} className="widget-preview" style={{ marginBottom: '16px' }}>
                                <WidgetRenderer 
                                  widget={widget}
                                  isSelected={false}
                                  onSelect={() => {}}
                                  onUpdate={() => {}}
                                  onDelete={() => {}}
                                />
                              </div>
                            ))
                          ) : (
                            <div 
                              style={{
                                padding: '40px',
                                textAlign: 'center',
                                color: '#999',
                                border: '2px dashed #e0e0e0',
                                borderRadius: '8px',
                                fontSize: '14px'
                              }}
                            >
                              Empty column - drag widgets here
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                {sections.length === 0 && (
                  <div 
                    style={{
                      textAlign: 'center',
                      padding: '60px',
                      color: '#999',
                      backgroundColor: '#fff',
                      borderRadius: '8px'
                    }}
                  >
                    No sections added yet. Start by adding a section to your page.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div 
              className="json-preview-container"
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div 
                className="json-controls"
                style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '16px',
                  justifyContent: 'flex-end'
                }}
              >
                <button 
                  className="updateProfile"
                  onClick={handleDownloadJSON}
                  title="Download JSON"
                >
                  ⬇️ Download JSON
                </button>
                <button 
                  className="updateProfile"
                  onClick={handleCopyJSON}
                  title="Copy to Clipboard"
                >
                  📋 Copy JSON
                </button>
              </div>
              <pre 
                className="json-preview"
                style={{
                  flex: 1,
                  margin: 0,
                  padding: '20px',
                  backgroundColor: '#282c34',
                  color: '#abb2bf',
                  borderRadius: '8px',
                  overflow: 'auto',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace'
                }}
              >
                <code>{JSON.stringify(formData, null, 2)}</code>
              </pre>
            </div>
          )}
        </div>
      </div>

      {/* Add animation keyframes to global styles */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideUp {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default PreviewModal;